/* eslint-disable react/no-unescaped-entities */
import React, { FC } from 'react'

import { Heading, Hero, Page, Text } from '../components'

export const PrivacyPolicyPage: FC = () => (
  <Page wordyPage>
    <Hero mb="6" showAboutSection={false} />
    <Heading as="h1" mb="4">
      Privacy Policy
    </Heading>
    <Heading as="h2" mb="4">
      Overview
    </Heading>
    <Text mb="3">
      Wave Snippets is a service that creates gorgeous animated snippets of your
      code to share with the world and is operated by Caldera, LLC. Caldera, LLC
      (alternately, "Caldera", "we", "us" or "our") respects your privacy and is
      committed to complying with this privacy policy ("Privacy Policy"), which
      describes what information we collect about you, including how we collect
      it, how we use it, with whom we may share it and what choices you have
      regarding our use of your information. This Privacy Policy applies to
      information collected on our website located at www.wavesnippets.com (the
      "Website"), our mobile apps, and/or other services we provide
      (collectively, “Wave Snippets”).
    </Text>
    <Heading as="h2" mb="4">
      Changes to our Privacy Policy
    </Heading>
    <Text mb="3">
      We may change this Privacy Policy at any time and from time to time. The
      most recent version of the Privacy Policy is reflected by the version date
      located at the bottom of this Privacy Policy. All such updates and
      amendments are effective immediately upon notice thereof, which we may
      give by any means, including, but not limited to, by posting a revised
      version of this Privacy Policy or other notice on the Website. You should
      view this Privacy Policy often to stay informed of changes that may affect
      you, as your continued use of the Website signifies your continuing
      consent to be bound by this Privacy Policy. We expressly reserve the right
      to make any changes to this Privacy Policy at any time, without prior
      notice to you. The version of this Privacy Policy posted here on each
      respective date you visit the Website shall be the Privacy Policy
      applicable to your access and use of the Website on that date. Our
      electronically or otherwise properly stored copies of this Privacy Policy
      shall be deemed to be the true, complete, valid, authentic, and
      enforceable copy of the version of this Privacy Policy which were in force
      on each respective date you visited the Website. This Privacy Policy is
      not intended to and does not create any contractual or other legal right
      in or on behalf of any party other than Wave Snippets.
    </Text>
    <Heading as="h2" mb="4">
      What Information Do We Collect?
    </Heading>
    <Text mb="3">
      As described in detail below, we may collect certain identifying
      information from or about you in connection with your use of, or your
      submissions to, the Website (collectively, the "Collected Information").
      Collected Information includes, without limitation, any information that
      may be used to identify an individual, including, but not limited to, a
      first and last name, a home, business, or other physical address, a phone
      number, an email address, employer name, employer information (i.e.,
      employer address and other identifying information), and credit card
      number (and related information such as expiration date). Wave Snippets
      uses the information we collect to provide the services that are available
      via the Website.
    </Text>
    <Text mb="3">
      We require an email address from you when you register with certain Wave
      Snippets apps. We use your email for both, "functions" (e.g. sending you
      your snippet exports), “administrative” (e.g., confirming your
      registration, notifying of matches) and “promotional” (e.g., newsletters,
      new product offerings, special discounts, event notifications, special
      third- party offers) purposes. Email messages we send you may contain code
      that enables our database to track your usage of the e-mails, including
      whether the e-mail was opened and what links (if any) were clicked. You
      may opt-out of receiving promotional emails and other promotional
      communications from us at any time via the opt-out links provided in such
      communications, or by e-mailing{' '}
      <a href="mailto:wavesnippets@gmail.com">wavesnippets@gmail.com</a> with
      your specific request. However, we reserve the right to send you certain
      communications relating to Wave Snippets such as service announcements,
      security alerts, update notices, or other administrative messages) without
      affording you the opportunity to opt out of receiving such communications.
    </Text>
    <Text mb="3">
      We may periodically conduct voluntary member surveys. We encourage our
      members to participate in such surveys because they provide us with
      important information regarding the improvement of Wave Snippets. We do
      not link the survey responses to your name or email address, and all
      responses are anonymous.
    </Text>
    <Text mb="3">
      To ensure we are providing a quality service we also collect:
      <ol>
        <li>
          Device Information/Specifications. Technical information about your
          computer or mobile device (e.g., type of device, web browser or
          operating system, IP address) to analyze trends, administer the site,
          prevent fraud, track visitor movement in the aggregate, and gather
          broad demographic information.
        </li>
        <li>
          Length and Extent of Usage. How long and in what manner you used Wave
          Snippets and which services and features you used.
        </li>
      </ol>
    </Text>
    <h3>Cookies</h3>
    <Text mb="3">
      We use “cookies” to keep track of some types of information while you are
      visiting Wave Snippets or using our services. “Cookies” are very small
      files placed on your computer, and they allow us to count the number of
      visitors to our Website and distinguish repeat visitors from new visitors.
      They also allow us and third parties we work with to save user
      preferences, track user trends, and advertise to you. We use both session
      and persistent cookies on our Website; session cookies expire at the end
      of a particular visit to our Website, while persistent cookies (also
      called stored cookies) remain active until you disable them through your
      browser settings, or until a pre-set expiration date. We rely on cookies
      for the proper operation of Wave Snippets; therefore if your browser is
      set to reject all cookies, Wave Snippets may not function properly. Users
      who refuse cookies assume all responsibility for any resulting loss of
      functionality with respect to Wave Snippets.
    </Text>
    <h3>Web Beacons</h3>
    <Text mb="3">
      “Web beacons” (also known as “clear gifs” and “pixel tags”) are small
      transparent graphic images that are often used in conjunction with cookies
      in order to further personalize Wave Snippets for our users, collect a
      limited set of information about our visitors, and advertise to our users.
      We may also use web beacons in email communications in order to understand
      the behavior of our customers, such as whether an email has been opened or
      acted upon.
    </Text>
    <Heading as="h2" mb="4">
      How Can I Opt-Out of Promotional Correspondence?
    </Heading>
    <Text mb="3">
      We may send you emails with promotional offers. If you would no longer
      like to receive information, newsletters or other promotional messages
      from us, please click the link at the bottom of any such email you receive
      from us and follow the instructions, or alternatively you may contact us,{' '}
      <a href="mailto:wavesnippets@gmail.com">wavesnippets@gmail.com</a> with
      the word "UNSUBSCRIBE" or "REMOVE" in the subject line. Your email address
      will be removed from our marketing list. Please allow us a reasonable
      period of time in order to satisfy your request, as some promotions may
      already be in process.
    </Text>

    <Heading as="h2" mb="4">
      Online Account Management
    </Heading>
    <Text mb="3">
      If you elected to subscribe to any online services provided via the
      Website, including the ability to access any password-protected areas of
      the Website, you will be required to submit personal information
      (including some Collected Information) for the purpose of verifying your
      identity and your authority to manage the account. This Collected
      Information will be used by Wave Snippets, our agents, suppliers and
      third-party service providers for internal use, to respond to your service
      requests and for any other lawful purpose.
    </Text>
    <Heading as="h2" mb="4">
      How do we use your information?
    </Heading>
    <Text mb="3">
      If you are a visitor of our website, we use your personal and non-personal
      data to engage with you and support live chat conversations on the
      website. When you are one of our customers, we use the information we
      collect about you to provide the services to you. As part of that purpose,
      we use your personal data and non-personal data:
    </Text>
    <ul>
      <li>
        to create and maintain your platform account, and to control access to
        it.
      </li>
      <li>
        to personalize your experience on the platform based on your job
        preferences
      </li>
      <li>
        to respond to any requests you may submit for support, purchase of
        additional services, or similar communications
      </li>
      <li>
        for billing and collection purposes, if you have subscribed to one of
        our paid plans
      </li>
      <li>
        to communicate with you (for example through newsletters, marketing
        emails, announcements or special offers) about our services
      </li>
      <li>
        for the investigation and prevention of fraud and breaches of the terms
        of service
      </li>
      <li>to enable third parties to provide services to us</li>
      <li>to comply with applicable laws to which we are subject</li>
      <li>where you otherwise consent to such use</li>
    </ul>
    <Text mb="3">
      We may use your non-personal data to enhance the services, for instance
      through web analytics or troubleshooting. We may also use aggregated or
      depersonalized information to promote our services, such as by citing
      usage statistics.
    </Text>
    <Text mb="3">
      Use for Research. In addition to the uses outlined above, by using Wave
      Snippets, you agree to allow us to anonymously use the information from
      you and your experiences to continue our research into creating an open
      environment for learning to code and educating others. This research may
      be published in our blogs or interviews. However, all of your responses
      will be kept anonymous, and we assure you that no personal information
      will be published.
    </Text>
    <Heading as="h2" mb="4">
      With whom do we share personal data?
    </Heading>
    <Text mb="3">
      <Text mb="3">
        Except for the limited circumstances we described here or in applicable
        agreement / terms of service, we do not share your personal data with
        third parties. When we need to provide your personal data to third
        parties, we will only share it to the extent necessary to provide you
        with our services. We may also share your personal data as required or
        permitted by law and as described below.
      </Text>
      <Text mb="3">
        We host the website and operate the platform using third parties,
        including AWS®, GCP®, Mailgun® and Netlify®. Your data will be hosted in
        their data centers throughout the United States and/or Europe.
      </Text>
      <Text mb="3">
        We may use Stripe®, to process subscription payments, and therefore
        provide them with the personal data required to charge your credit card.
      </Text>
      <Text mb="3">
        We may use third-party services either embedded into our website (such
        as Disqus®, Drift, Optimizely, Firebase, Segment™ and Google® Analytics)
        or outside of it (such as GitHub® and Twitter®) to communicate with you
        or to enhance the function of the website and the services.
      </Text>
      <Text mb="3">
        We may use third-party service providers and platforms (such as Pendo,
        Customer.io, Hubspot, Salesforce, Uservoice, Zapier, ConvertKit, and
        Zendesk) for customer engagement, customer chat, product feedback and
        customer support ticketing.
      </Text>
      <Text mb="3">
        While we provide these third parties with no more information than what
        is necessary to enable them to provide the services to us, any
        information that you provide these services providers independently is
        subject to their respective privacy policies and practices.
      </Text>
      <Text mb="3">
        In certain situations, Wave Snippets may be required to disclose
        personal data in response to lawful requests by public authorities,
        including to meet national security or law enforcement requirements.
      </Text>
      <Text mb="3">
        Additionally, we will provide information to a third party in the event
        of any reorganization, merger, sale, joint venture, assignment, transfer
        or other disposition of all or any portion of our business, assets or
        stock (including in connection with any bankruptcy or similar
        proceedings).
      </Text>
    </Text>
    <Heading as="h2" mb="4">
      For how long do we retain your personal data?
    </Heading>
    <Text mb="3">
      We keep your personal data for as long as is necessary to provide our
      services to you. If you would like us to cease all of the described uses
      of your personal data, you may delete your account at any time by sending
      an email to{' '}
      <a href="mailto:wavesnippets@gmail.com">wavesnippets@gmail.com</a> with
      the subject DELETE ACCOUNT. This will delete your personal data from our
      records, and we will make no further use of it. We may, however, retain
      copies of your personal data in backups. Please note that we may be
      required to retain certain information by law and/or for own legitimate
      business purposes.
    </Text>
    <Heading as="h2" mb="4">
      Using Wave Snippets from outside the United States
    </Heading>
    <Text mb="3">
      If you are visiting Wave Snippets from outside the United States, please
      be aware that your information may be transferred to, stored, and
      processed globally, including in the United States where our servers are
      located and our central database is operated. By using our services, you
      understand and agree that your information may be transferred to our
      facilities and those third parties with whom we share it for processing as
      described in this Privacy Policy. Where required by law, international
      transfers of personal information will be supported by an adequacy
      mechanism.
    </Text>
    <Heading as="h2" mb="4">
      Referring Your Friends
    </Heading>
    <Text mb="3">
      We encourage you to refer your friends to Wave Snippets by sending us your
      friends’ email addresses or phone numbers. We will keep this information
      in our database, and enable you to send these friends a one- time email or
      text message from your device containing your name and inviting them to
      visit our site. This email will also include instructions on how to opt
      out and unsubscribe from our email list. You agree that you will not abuse
      this feature by entering the email addresses or phone numbers of those
      individuals who would not be interested in Wave Snippets.
    </Text>
    <Heading as="h2" mb="4">
      Age Restriction
    </Heading>
    <Text mb="3">
      We do not target or allow persons under 18 years of age to use Wave
      Snippets, and we do not knowingly collect information from persons under
      the age of 16. If you are a parent or legal guardian who discovers that
      your child has provided us with information without your consent, you may
      contact us at{' '}
      <a href="http://wavesnippets.com">http://wavesnippets.com</a>, and we will
      promptly delete such information from our files.
    </Text>
    <Heading as="h2" mb="4">
      Third Party Sites
    </Heading>
    <Text mb="3">
      Wave Snippets may contain links to other websites and services. If you
      choose to click on a third party link, you will be directed to that third
      party’s website or service. The fact that we link to a website or service
      is not an endorsement, authorization or representation of our affiliation
      with that third party, nor is it an endorsement of their privacy or
      information security policies or practices. We do not exercise control
      over third party websites or services. These third parties may place their
      own cookies or other files on your computer, collect data or solicit
      personal information from you. Other websites and services follow
      different rules regarding the use or disclosure of the personal
      information you submit to them. We encourage you to read the privacy
      policies or statements of the other websites and services you visit.
    </Text>
    <Heading as="h2" mb="4">
      Securing Your Personal Information
    </Heading>
    <Text mb="3">
      We take steps to ensure that your information is treated securely and in
      accordance with this Privacy Policy. Unfortunately, the Internet cannot be
      guaranteed to be 100% secure, and we cannot ensure or warrant the security
      of any information you provide to us. We do not accept liability for
      unintentional disclosure.
    </Text>
    <Text mb="3">
      By providing personal information to us, you agree that we may communicate
      with you electronically regarding security, privacy, and administrative
      issues relating to your use of Wave Snippets. If we learn of a security
      system’s breach, we may attempt to notify you electronically by posting a
      notice on the Site or sending an e-mail to you. You may have a legal right
      to receive this notice in writing.
    </Text>
    <Heading as="h2" mb="4">
      Correspondence
    </Heading>
    <Text mb="3">
      We appreciate your questions and comments about the Website and welcome
      your messages at our our support email address,{' '}
      <a href="mailto:wavesnippets@gmail.com">wavesnippets@gmail.com</a>. If you
      correspond with Wave Snippets through the Website or via email, the
      Collected Information may include the content of, and metadata regarding,
      any correspondence you may have with us. We may share your messages with
      those within our organization who are most capable of addressing the
      issues contained in your message. We may archive your message for a
      certain period of time or discard it.
    </Text>
    <Text mb="3">
      You are free to refuse our request for your personal information, with the
      understanding that we may be unable to provide you with some of your
      desired services.
    </Text>
    <Text mb="3">
      Your continued use of our website will be regarded as acceptance of our
      practices around privacy and personal information. If you have any
      questions about how we handle user data and personal information, feel
      free to contact us.
    </Text>
    <Text mb="3">
      This policy is effective as of <b>8 July 2020</b>.
    </Text>
  </Page>
)
